import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import PhoneMock from "@/components/PhoneMock";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Printer,
  ClipboardList,
  Hammer,
  KeyRound,
  CheckCircle2,
  Send,
  AlertTriangle,
  DollarSign,
  Clock,
  ShieldCheck,
  PhoneCall,
} from "lucide-react";

function money(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

// Days-since-ready color for Ready-for-Pickup cards.
function ageStyle(days) {
  if (days == null) return "";
  if (days <= 1) return "bg-emerald-50 border-l-4 border-l-emerald-500 dark:bg-emerald-950/20";
  if (days <= 3) return "bg-yellow-50 border-l-4 border-l-yellow-500 dark:bg-yellow-950/20";
  return "bg-red-50 border-l-4 border-l-red-500 dark:bg-red-950/20";
}
function ageDot(days) {
  if (days == null) return "bg-neutral-400";
  if (days <= 1) return "bg-emerald-500";
  if (days <= 3) return "bg-yellow-500";
  return "bg-red-500";
}

const COLUMNS = [
  { key: "estimating", title: "Estimating", sub: "Awaiting write-up", Icon: ClipboardList, tint: "bg-[hsl(var(--gold))] text-neutral-900" },
  { key: "in_repair", title: "In Repair", sub: "On the floor", Icon: Hammer, tint: "bg-blue-600 text-white" },
  { key: "ready_pickup", title: "Ready for Pickup", sub: "Fire the text!", Icon: KeyRound, tint: "bg-red-600 text-white" },
  { key: "delivered", title: "Delivered", sub: "RO closed", Icon: CheckCircle2, tint: "bg-emerald-600 text-white" },
];

export default function BodyShopPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [firedIds, setFiredIds] = useState(new Set());

  const load = () => {
    setBusy(true);
    return api
      .get("/body-shop/pipeline")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
  }, []);

  const columns = useMemo(() => {
    if (!data) return {};
    const g = { estimating: [], in_repair: [], ready_pickup: [], delivered: [] };
    for (const j of data.jobs) {
      if (g[j.status]) g[j.status].push(j);
    }
    return g;
  }, [data]);

  // 2-Day Follow-Up: cars checked in for additional bodywork that have been
  // sitting in Estimating for 2+ days. Someone needs to call the customer to
  // walk them through the supplemental repair estimate.
  const followUps = useMemo(() => {
    if (!data?.jobs) return [];
    return data.jobs
      .filter((j) => j.status === "estimating" && (j.days_in_shop || 0) >= 2)
      .map((j) => ({ ...j, age_days: j.days_in_shop || 0 }))
      .sort((a, b) => b.age_days - a.age_days);
  }, [data]);

  const fireText = async (jobId) => {
    try {
      await api.post(`/body-shop/${jobId}/fire-ready-text`);
      const next = new Set(firedIds);
      next.add(jobId);
      setFiredIds(next);
      toast.success("Ready-for-pickup text queued to customer");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const totals = data?.totals || {};
  const dealer = data?.dealership_name || "Your Dealership";
  const sampleReady = columns.ready_pickup?.[0];
  const previewBody = sampleReady
    ? `Great news ${sampleReady.customer_name.split(" ")[0]} — your ${sampleReady.vehicle} is ready for pickup at ${dealer} Body Shop! Tap to schedule pickup or call your estimator.`
    : `Great news {first_name} — your {vehicle} is ready for pickup at ${dealer} Body Shop! Tap to schedule pickup or call your estimator.`;

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Body Shop Pipeline" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Collision · Ready-for-Pickup Automation</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Body Shop Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            Every collision job from <b>estimate → in repair → ready → delivered</b>. The moment a
            car hits Ready, the customer gets a text. <b>No more forgotten cars sitting overnight
            waiting on a phone call your estimator never made.</b>
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-bodyshop-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      {/* 2-Day Follow-Up strip — cars sitting in Estimating for 2+ days that
          need a customer call about supplemental damage. Positive framing:
          "make the call now" not "you missed it". */}
      {followUps.length > 0 && (
        <Card
          className="p-4 mb-6 border-l-4 border-l-amber-500 bg-amber-50/60 dark:bg-amber-950/20"
          data-testid="bs-followup-strip"
        >
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <div className="w-8 h-8 rounded-md bg-amber-500 grid place-items-center shrink-0">
              <Clock className="w-4 h-4 text-white" strokeWidth={2.4} />
            </div>
            <div className="font-display font-black text-lg text-amber-900 dark:text-amber-200">
              2-Day Follow-Up Calls Due ({followUps.length})
            </div>
            <div className="font-mono-label text-[0.6rem] tracking-widest text-amber-800 dark:text-amber-300 ml-1">
              CHECKED IN &gt; 2 DAYS AGO · CALL CUSTOMER ABOUT ADDITIONAL WORK
            </div>
          </div>
          <p className="text-xs text-amber-900/80 dark:text-amber-200/80 mb-3 leading-relaxed">
            These customers dropped off their car for repair and we've found additional bodywork.
            Two days in, someone needs to call them — walk through the supplemental estimate,
            get approval, and keep the job moving. Silence loses the job.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {followUps.slice(0, 9).map((j) => (
              <div
                key={j.id}
                className="rounded-md border-2 border-amber-300 dark:border-amber-800 bg-white dark:bg-neutral-900 p-3"
                data-testid={`bs-followup-${j.id}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <div className="font-bold text-sm text-neutral-900 dark:text-white truncate">
                    {j.customer_name}
                  </div>
                  <div className="font-display font-black text-base tabular-nums text-amber-700 shrink-0 ml-2">
                    {j.age_days}d
                  </div>
                </div>
                <div className="text-[0.7rem] text-neutral-700 dark:text-neutral-300 mb-1.5 truncate">
                  {j.vehicle}
                </div>
                <div className="text-[0.65rem] text-amber-800 dark:text-amber-300 font-mono">
                  Est ${(j.estimate_total || 0).toLocaleString()} · {j.estimator_name || "unassigned"}
                </div>
                {j.customer_phone && (
                  <a
                    href={`tel:${j.customer_phone.replace(/[^0-9+]/g, "")}`}
                    className="mt-2 flex items-center justify-center gap-1 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-[0.72rem] font-semibold py-1.5 transition-all"
                    data-testid={`bs-followup-call-${j.id}`}
                  >
                    <PhoneCall className="w-3 h-3" strokeWidth={2.4} />
                    Call {j.customer_phone}
                  </a>
                )}
              </div>
            ))}
          </div>
          {followUps.length > 9 && (
            <div className="mt-3 text-center text-[0.7rem] text-amber-700 dark:text-amber-300 font-mono-label tracking-widest">
              + {followUps.length - 9} MORE — see Estimating column below
            </div>
          )}
        </Card>
      )}

      {/* KPI dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card className="p-4 border-border !bg-[hsl(var(--gold))]/10 border-[hsl(var(--gold))]/40" data-testid="bs-kpi-estimating">
          <div className="font-mono-label text-neutral-900 text-[0.6rem]">Estimating</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums">
            {totals.estimating ?? 0}
          </div>
          <div className="text-[0.65rem] text-neutral-700 mt-0.5">awaiting write-up</div>
        </Card>
        <Card className="p-4 border-border" data-testid="bs-kpi-in-repair">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">In Repair</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums text-blue-600">
            {totals.in_repair ?? 0}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-0.5">on the floor</div>
        </Card>
        <Card className="p-4 border-border !bg-red-50 dark:!bg-red-950/20" data-testid="bs-kpi-ready">
          <div className="font-mono-label text-red-700 dark:text-red-300 text-[0.6rem] flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
            Ready · Text Now
          </div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums text-red-700 dark:text-red-300">
            {totals.ready_pickup ?? 0}
          </div>
          <div className="text-[0.65rem] text-red-700/70 dark:text-red-300/70 mt-0.5">customer waiting</div>
        </Card>
        <Card className="p-4 border-border" data-testid="bs-kpi-delivered">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Delivered</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums text-emerald-600">
            {totals.delivered ?? 0}
          </div>
          <div className="text-[0.65rem] text-muted-foreground mt-0.5">this month</div>
        </Card>
        <Card className="p-4 border-border !bg-[hsl(var(--gold))]/15 border-[hsl(var(--gold))]/50" data-testid="bs-kpi-cycle">
          <div className="font-mono-label text-[0.6rem] text-neutral-900 flex items-center gap-1">
            <Clock className="w-3 h-3" strokeWidth={2} />
            Avg Cycle
          </div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1 tabular-nums">
            {totals.avg_cycle_days ?? "—"} d
          </div>
          <div className="text-[0.65rem] text-neutral-700 mt-0.5">check-in → delivered</div>
        </Card>
      </div>

      {/* Value strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <Card className="p-4 border-l-4 border-l-red-500 bg-red-50/60 dark:bg-red-950/20" data-testid="bs-val-inshop">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-red-500 grid place-items-center shrink-0">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div className="font-mono-label text-red-800 dark:text-red-300 text-[0.6rem] tracking-widest">
              Value $ In Shop Right Now
            </div>
          </div>
          <div className="font-display font-black text-3xl tabular-nums tracking-tighter text-red-800 dark:text-red-200">
            {money(totals.in_shop_value)}
          </div>
          <div className="text-[0.65rem] text-red-700/80 dark:text-red-300/70 mt-1 leading-snug">
            Estimating + In Repair + Ready for Pickup. Every day this sits, you're paying storage
            and your customer&apos;s waiting.
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/20" data-testid="bs-val-delivered">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-emerald-600 grid place-items-center shrink-0">
              <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div className="font-mono-label text-emerald-800 dark:text-emerald-300 text-[0.6rem] tracking-widest">
              Delivered · Collected
            </div>
          </div>
          <div className="font-display font-black text-3xl tabular-nums tracking-tighter text-emerald-800 dark:text-emerald-200">
            {money(totals.delivered_value)}
          </div>
          <div className="text-[0.65rem] text-emerald-700/80 dark:text-emerald-300/70 mt-1 leading-snug">
            Cars picked up. Cash in the bank. Insurance paid.
          </div>
        </Card>
      </div>

      {/* Oldest-ready alert */}
      {totals.oldest_ready_days > 0 && (
        <Card
          className="p-3 mb-6 border-l-4 border-l-[hsl(var(--gold))] bg-[hsl(var(--gold))]/[0.05] flex items-center gap-3"
          data-testid="bs-oldest-ready"
        >
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" strokeWidth={2} />
          <div className="flex-1 text-sm">
            Oldest ready-for-pickup car has been waiting{" "}
            <b className="text-red-600 tabular-nums">{totals.oldest_ready_days} days</b> — text
            that customer right now.
          </div>
        </Card>
      )}

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6" data-testid="bs-kanban">
        {COLUMNS.map((col) => {
          const list = columns[col.key] || [];
          return (
            <div key={col.key} className="min-w-0" data-testid={`bs-col-${col.key}`}>
              <div className={`px-3 py-2 rounded-t-md flex items-center gap-2 ${col.tint}`}>
                <col.Icon className="w-4 h-4" strokeWidth={2.2} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{col.title}</div>
                  <div className="text-[0.55rem] opacity-85 truncate">{col.sub}</div>
                </div>
                <div className="font-display font-black text-lg tabular-nums">{list.length}</div>
              </div>
              <div className="border border-t-0 border-border rounded-b-md p-2 bg-muted/20 space-y-2 min-h-[240px]">
                {list.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-8">
                    Nothing in this stage.
                  </div>
                )}
                {list.map((j) => {
                  const isReady = col.key === "ready_pickup";
                  const days = j.days_since_ready;
                  const fired = firedIds.has(j.id);
                  return (
                    <div
                      key={j.id}
                      className={`rounded-md p-3 border border-border bg-background shadow-sm ${
                        isReady ? ageStyle(days) : ""
                      }`}
                      data-testid={`bs-card-${j.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display font-bold text-sm truncate">
                            {j.customer_name}
                          </div>
                          <div className="text-[0.65rem] text-muted-foreground truncate">
                            {j.vehicle}
                          </div>
                        </div>
                        {isReady && (
                          <div className="flex flex-col items-end shrink-0">
                            <span className={`w-2 h-2 rounded-full ${ageDot(days)}`} aria-hidden />
                            <div className="font-mono-label text-[0.5rem] text-muted-foreground mt-0.5 tabular-nums">
                              {days}d
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs font-medium leading-snug">{j.damage_type}</div>
                      <div className="mt-0.5 text-[0.6rem] text-muted-foreground">
                        {j.insurance} · <span className="font-mono">{j.claim_number}</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[0.6rem] text-muted-foreground tabular-nums">
                        <span>Est {money(j.estimate_total)}</span>
                        <span>{j.days_in_shop}d in shop</span>
                      </div>
                      <div className="mt-1.5 text-[0.6rem] text-muted-foreground">
                        Estimator: {j.estimator_name}
                      </div>

                      {isReady && (
                        <Button
                          size="sm"
                          onClick={() => fireText(j.id)}
                          disabled={fired}
                          className={`w-full mt-2 h-7 text-[0.65rem] ${
                            fired
                              ? "bg-emerald-600 hover:bg-emerald-600 text-white"
                              : "bg-red-600 hover:bg-red-700 text-white"
                          }`}
                          data-testid={`bs-fire-${j.id}`}
                        >
                          {fired ? (
                            <>
                              <CheckCircle2 className="w-3 h-3 mr-1" strokeWidth={2.5} />
                              Sent
                            </>
                          ) : (
                            <>
                              <Send className="w-3 h-3 mr-1" strokeWidth={2.5} />
                              Fire Ready Text
                            </>
                          )}
                        </Button>
                      )}
                      {col.key === "delivered" && j.delivered_on && (
                        <div className="mt-2 text-[0.6rem] text-emerald-700 dark:text-emerald-300 font-semibold tabular-nums">
                          Delivered {new Date(j.delivered_on).toLocaleDateString([], { month: "short", day: "numeric" })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Example text preview */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mb-6">
        <div>
          <Card className="p-5 border-border">
            <div className="font-mono-label text-muted-foreground text-[0.6rem] mb-1">
              The Text Every Ready Card Fires
            </div>
            <div className="font-display font-bold text-lg tracking-tight mb-3">
              &quot;Your car is ready for pickup&quot;
            </div>
            <div className="text-sm text-muted-foreground leading-relaxed">
              The second an estimator drops a car into Ready-for-Pickup, this text fires. Two
              buttons: <b>Schedule Pickup</b> or <b>Call Estimator</b>. Zero phone tag. The
              customer knows the moment you know.
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="p-3 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20">
                <div className="font-mono-label text-[0.55rem] text-emerald-700 tracking-widest">
                  0 – 1 day
                </div>
                <div className="font-display font-black text-sm text-emerald-800 mt-1">
                  Green — Fresh
                </div>
              </div>
              <div className="p-3 rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20">
                <div className="font-mono-label text-[0.55rem] text-yellow-800 tracking-widest">
                  2 – 3 days
                </div>
                <div className="font-display font-black text-sm text-yellow-800 mt-1">
                  Yellow — Text
                </div>
              </div>
              <div className="p-3 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/20">
                <div className="font-mono-label text-[0.55rem] text-red-700 tracking-widest">
                  4+ days
                </div>
                <div className="font-display font-black text-sm text-red-800 mt-1">
                  Red — Call Now
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="flex justify-center">
          <PhoneMock
            dealer={dealer}
            from="+1 (913) 555-0100 · Body Shop"
            body={previewBody}
            sent="Now"
            label="Body Shop · Ready for Pickup"
            accent="bg-red-600 text-white"
            scheduleUrl="https://drvlgc.co/s/bs"
            callUrl="https://drvlgc.co/c/bs"
            testid="bs-phone-mock"
          />
        </div>
      </div>
    </DashboardShell>
  );
}
